(() => {
  const SELECTORS = {
    root: '[data-delivery-zip-checker]',
    form: '[data-delivery-zip-form]',
    input: '[data-delivery-zip-input]',
    submit: '[data-delivery-zip-submit]',
    message: '[data-delivery-zip-message]',
  };

  const TEXTS = {
    success: 'Dostarczamy pod Twój adres',
    error: 'Niestety nie dostarczamy pod Twój adres',
    invalid: 'Wpisz poprawny kod pocztowy (np. 00-000)',
    loading: 'Sprawdzamy dostępność dostawy...',
    loadError: 'Nie można sprawdzić kodu. Spróbuj ponownie za chwilę.',
  };

  const setCacheByUrl = new Map();

  const normalizeZip = (value) => {
    const digits = (value || '').replace(/\D/g, '');
    if (digits.length !== 5) return null;
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  };

  const formatZipDraft = (value) => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 5);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  };

  const setMessage = (messageNode, kind, text) => {
    if (!messageNode) return;
    messageNode.classList.remove('is-success', 'is-error', 'is-neutral');
    messageNode.classList.add(kind);
    messageNode.textContent = text;
  };

  const parseZipText = (content) => {
    const result = new Set();
    content.split(/\r?\n/).forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      if (/^\d{2}-\d{3}$/.test(trimmed)) {
        result.add(trimmed);
      }
    });
    return result;
  };

  const parseZipJson = (content) => {
    const payload = JSON.parse(content);
    if (!Array.isArray(payload)) return new Set();
    const result = new Set();

    payload.forEach((item) => {
      const normalized = normalizeZip(String(item));
      if (normalized) result.add(normalized);
    });

    return result;
  };

  const loadZipSet = async (url) => {
    if (!url) throw new Error('Missing zip asset URL.');

    if (setCacheByUrl.has(url)) {
      return setCacheByUrl.get(url);
    }

    const promise = fetch(url, { cache: 'force-cache' })
      .then((response) => {
        if (!response.ok) throw new Error(`ZIP asset request failed: ${response.status}`);
        return response.text();
      })
      .then((content) => {
        if (url.endsWith('.json')) return parseZipJson(content);
        return parseZipText(content);
      });

    setCacheByUrl.set(url, promise);
    return promise;
  };

  const readStorage = (key) => {
    if (!key) return null;
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const writeStorage = (key, value) => {
    if (!key) return;
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // no-op for private mode / blocked storage
    }
  };

  const applySavedState = (root, input, messageNode) => {
    const valueKey = root.dataset.storageKey || 'delivery_zip';
    const statusKey = root.dataset.storageStatusKey || 'delivery_zip_status';
    const savedZip = readStorage(valueKey);
    const savedStatus = readStorage(statusKey);

    if (!savedZip || !input) return;
    input.value = savedZip;

    if (savedStatus === '1') {
      setMessage(messageNode, 'is-success', TEXTS.success);
      root.dataset.deliveryZipResult = 'deliverable';
    }

    if (savedStatus === '0') {
      setMessage(messageNode, 'is-error', TEXTS.error);
      root.dataset.deliveryZipResult = 'undeliverable';
    }
  };

  const initWidget = (root) => {
    if (!root || root.dataset.deliveryZipReady === 'true') return;

    const input = root.querySelector(SELECTORS.input);
    const submit = root.querySelector(SELECTORS.submit);
    const messageNode = root.querySelector(SELECTORS.message);
    const zipsUrl = root.dataset.zipsUrl;

    if (!input || !submit || !messageNode || !zipsUrl) return;

    root.dataset.deliveryZipReady = 'true';
    applySavedState(root, input, messageNode);

    input.addEventListener('input', () => {
      const formatted = formatZipDraft(input.value);
      if (formatted !== input.value) input.value = formatted;
    });

    const checkZip = async () => {
      const normalized = normalizeZip(input.value);

      if (!normalized) {
        setMessage(messageNode, 'is-neutral', TEXTS.invalid);
        root.dataset.deliveryZipResult = 'invalid';
        return;
      }

      input.value = normalized;
      setMessage(messageNode, 'is-neutral', TEXTS.loading);
      submit.disabled = true;

      try {
        const zipSet = await loadZipSet(zipsUrl);
        const found = zipSet.has(normalized);
        const messageKind = found ? 'is-success' : 'is-error';
        const messageText = found ? TEXTS.success : TEXTS.error;

        setMessage(messageNode, messageKind, messageText);

        const valueKey = root.dataset.storageKey || 'delivery_zip';
        const statusKey = root.dataset.storageStatusKey || 'delivery_zip_status';

        writeStorage(valueKey, normalized);
        writeStorage(statusKey, found ? '1' : '0');
        root.dataset.deliveryZipResult = found ? 'deliverable' : 'undeliverable';
      } catch (error) {
        setMessage(messageNode, 'is-neutral', TEXTS.loadError);
        root.dataset.deliveryZipResult = 'error';
      } finally {
        submit.disabled = false;
      }
    };

    submit.addEventListener('click', (event) => {
      event.preventDefault();
      checkZip();
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        checkZip();
      }
    });
  };

  const initAllWidgets = () => {
    document.querySelectorAll(SELECTORS.root).forEach(initWidget);
  };

  const setupAutoInit = () => {
    initAllWidgets();

    const observer = new MutationObserver((mutations) => {
      let shouldInit = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches(SELECTORS.root)) shouldInit = true;
          if (node.querySelector && node.querySelector(SELECTORS.root)) shouldInit = true;
        });
      });

      if (shouldInit) initAllWidgets();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupAutoInit);
  } else {
    setupAutoInit();
  }
})();
